import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { AuthProvider } from "./auth/AuthProvider.js";
import { RequireAuth } from "./auth/RequireAuth.js";
import { AppShell } from "./components/shell/AppShell.js";
import { makeQueryClient } from "./lib/query-client.js";

import Login from "./pages/Login.js";
import AuthCallback from "./pages/AuthCallback.js";
import Dashboard from "./pages/Dashboard.js";
import ListingsList from "./pages/ListingsList.js";
import ListingNew from "./pages/ListingNew.js";
import ImportListings from "./pages/ImportListings.js";
import ListingEditor from "./pages/ListingEditor.js";
import ListingMedia from "./pages/ListingMedia.js";
import ListingSnapshots from "./pages/ListingSnapshots.js";
import ListingApprovals from "./pages/ListingApprovals.js";
import Inbox from "./pages/Inbox.js";
import Contacts from "./pages/Contacts.js";
import Deals from "./pages/Deals.js";
import Compliance from "./pages/Compliance.js";
import Agenda from "./pages/Agenda.js";
import DealProforma from "./pages/DealProforma.js";
import Alerts from "./pages/Alerts.js";
import Inquiries from "./pages/Inquiries.js";
import Bookings from "./pages/Bookings.js";
import SellerLeads from "./pages/SellerLeads.js";
import Valuations from "./pages/Valuations.js";
import BlogList from "./pages/BlogList.js";
import BlogEditor from "./pages/BlogEditor.js";
import Pages from "./pages/Pages.js";
import Portals from "./pages/Portals.js";
import Audit from "./pages/Audit.js";
import Settings from "./pages/Settings.js";

const qc = makeQueryClient();

export default function App(): JSX.Element {
  return (
    <QueryClientProvider client={qc}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />

            <Route element={<RequireAuth><AppShell /></RequireAuth>}>
              <Route index element={<Dashboard />} />
              <Route path="listings" element={<ListingsList />} />
              <Route path="listings/new" element={<ListingNew />} />
              <Route path="import" element={<ImportListings />} />
              <Route path="listings/:id" element={<ListingEditor />} />
              <Route path="listings/:id/media" element={<ListingMedia />} />
              <Route path="listings/:id/snapshots" element={<ListingSnapshots />} />
              <Route path="listings/:id/approvals" element={<ListingApprovals />} />
              <Route path="inbox" element={<Inbox />} />
              <Route path="contacts" element={<Contacts />} />
              <Route path="deals" element={<Deals />} />
              <Route path="deals/:id/proforma" element={<DealProforma />} />
              <Route path="adempimenti" element={<Compliance />} />
              <Route path="agenda" element={<Agenda />} />
              <Route path="alerts" element={<Alerts />} />
              <Route path="inquiries" element={<Inquiries />} />
              <Route path="bookings" element={<Bookings />} />
              <Route path="seller-leads" element={<SellerLeads />} />
              <Route path="valuations" element={<Valuations />} />
              <Route path="blog" element={<BlogList />} />
              <Route path="blog/:id" element={<BlogEditor />} />
              <Route path="pages" element={<Pages />} />
              <Route path="portals" element={<Portals />} />
              <Route path="audit" element={<Audit />} />
              <Route path="settings" element={<Settings />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="bottom-right" theme="system" />
      </AuthProvider>
    </QueryClientProvider>
  );
}
